import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Download, Loader2, Send, Scale, Search } from "lucide-react";
import { publicApi, mediaUrl } from "../lib/api";
import { getIcon } from "../lib/icons";
import Seo, { SITE_NAME, breadcrumbSchema, faqSchema } from "../components/Seo";
import { useToast } from "../hooks/use-toast";
import { FounderSection } from "../components/FounderSection";
import GoogleRatingBadge from "../components/GoogleRatingBadge";
import BookCallButton from "../components/BookCallButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import xeroLogo from "../assets/xero-logo.png";
import intouchLogo from "../assets/intouch-logo.png";
import MarketingConsent from "../components/MarketingConsent";
import AskCassieRelated from "../components/AskCassieRelated";
import { getAttribution } from "../lib/attribution";

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

const serviceSchema = (lc) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Legal Cashiering",
  name: "CASS Legal Cashiering",
  description: lc.seo?.description || lc.hero?.subtitle || "",
  areaServed: "GB",
  audience: { "@type": "Audience", audienceType: "Licensed Conveyancers" },
  provider: { "@id": `${origin()}/#organization` },
  url: `${origin()}/legal-cashiering`,
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Legal Cashiering Services",
    itemListElement: (lc.services || []).map((s) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: s.title, description: s.intro },
    })),
  },
});

const EnquiryForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", current_software: "InTouch", message: "", marketing_consent: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await publicApi.contact({ ...form, division: "Legal Cashiering", service: "Legal Cashiering", attribution: getAttribution() });
      toast({ title: "Thanks — we'll be in touch", description: "We've received your enquiry and will reply shortly." });
      setForm({ name: "", email: "", phone: "", business_name: "", current_software: "InTouch", message: "", marketing_consent: false });
    } catch {
      toast({ title: "Couldn't send", description: "Please check your details and try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-navy/5 shadow-xl p-7 md:p-9" data-testid="lc-enquiry-form">
      <h3 className="font-display font-extrabold text-2xl text-navy mb-1">Request a quote</h3>
      <p className="text-sm text-slate-500 mb-6">Every firm is different — tell us a little about yours and we'll come back with a tailored quote.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <input required placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="lc-name" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lc-email" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
        <input placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lc-phone" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
        <input placeholder="Firm name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="lc-firm" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
      </div>
      <textarea required rows={4} placeholder="Tell us about your firm, caseload and current setup…" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="lc-message" className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
      <div className="mt-4"><MarketingConsent checked={form.marketing_consent} onChange={(v) => set("marketing_consent", v)} testId="lc-enquiry-marketing-consent" /></div>
      <button type="submit" disabled={busy} data-testid="lc-submit" className="mt-5 inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 disabled:opacity-60">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Request a quote
      </button>
    </form>
  );
};

const LeadMagnetModal = ({ lm, open, onClose }) => {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const reset = () => { setName(""); setEmail(""); setConsent(false); setSubmitting(false); };
  const handleClose = () => { reset(); onClose(); };
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await publicApi.downloadLeadMagnet({ name, email, marketing_consent: consent });
      window.open(mediaUrl(res.file_url), "_blank", "noopener,noreferrer");
      toast({ title: "Enjoy your download!", description: "Your file should open in a new tab." });
      handleClose();
    } catch (err) {
      const msg = err?.response?.status === 429
        ? "Too many requests. Please try again shortly."
        : (err?.response?.data?.detail || "Something went wrong. Please try again.");
      toast({ title: "Couldn't get the download", description: msg, variant: "destructive" });
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md" data-testid="lc-lead-magnet-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-navy text-2xl">Get your free download</DialogTitle>
          <DialogDescription className="text-slate-500">
            {lm?.heading ? `Enter your details and we'll open "${lm.heading}" for you.` : "Enter your details to download."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="lc-lead-magnet-form">
          <div>
            <Label htmlFor="lm-name" className="text-navy font-semibold">Full Name</Label>
            <Input id="lm-name" data-testid="lc-lm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="lm-email" className="text-navy font-semibold">Email Address *</Label>
            <Input id="lm-email" data-testid="lc-lm-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@firm.co.uk" className="mt-1.5" />
          </div>
          <p className="text-xs text-slate-400">We'll only use your details to send you occasional helpful resources. No spam, unsubscribe anytime.</p>
          <MarketingConsent checked={consent} onChange={setConsent} testId="lc-lm-marketing-consent" />
          <Button type="submit" disabled={submitting} data-testid="lc-lm-submit" className="w-full bg-teal hover:bg-teal-dark text-white font-bold rounded-full py-6">
            {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Preparing...</> : <>Download now <Download size={16} className="ml-2" /></>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function LegalCashiering() {
  const [lc, setLc] = useState(null);
  const [founder, setFounder] = useState(null);
  const [faqQuery, setFaqQuery] = useState("");
  const [lmOpen, setLmOpen] = useState(false);

  useEffect(() => {
    publicApi.legalCashiering().then(setLc).catch(() => {});
    publicApi.homepage().then((hp) => setFounder(hp?.founder || null)).catch(() => {});
  }, []);

  if (!lc) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>;

  const jsonLd = [
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Legal Cashiering", path: "/legal-cashiering" }]),
    serviceSchema(lc),
    ...(lc.faqs?.length ? [faqSchema(lc.faqs)] : []),
  ];

  return (
    <div>
      <Seo title={lc.seo?.title || "Legal Cashiering for Licensed Conveyancers"} description={lc.seo?.description} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="numbers-pattern text-white relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <span className="inline-flex items-center gap-2 text-teal font-bold text-sm tracking-widest uppercase mb-4"><Scale size={16} /> {(() => {
            const eb = lc.hero?.eyebrow || "";
            return /^cass\b/i.test(eb)
              ? <><span className="text-orange">{eb.slice(0, 4)}</span>{eb.slice(4)}</>
              : eb;
          })()}</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">{lc.hero?.title}</h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">{lc.hero?.subtitle}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#enquire" data-testid="lc-hero-cta" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">{lc.hero?.cta_label} <ArrowRight size={16} /></a>
            <BookCallButton testId="lc-book-call" label="Book a Call" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white font-bold text-sm px-6 py-3.5 rounded-full transition-all bg-transparent" />
            {lc.lead_magnet?.enabled && (
              <a href="#lead-magnet" className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white font-bold text-sm px-6 py-3.5 rounded-full transition-all"><Download size={16} /> {lc.hero?.guide_label}</a>
            )}
          </div>
          <div className="mt-6"><GoogleRatingBadge dark /></div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2">
            {(lc.trust_bar || []).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200" data-testid={`lc-trust-${i}`}><CheckCircle2 size={15} className="text-teal" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy">{lc.problem?.heading}</h2>
          <p className="mt-4 text-base text-slate-500 leading-relaxed max-w-3xl">{lc.problem?.intro}</p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-4">
            {(lc.problem?.points || []).map((p, i) => (
              <li key={i} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-navy/5"><CheckCircle2 size={18} className="text-teal shrink-0 mt-0.5" /> <span className="text-sm font-semibold text-navy">{p}</span></li>
            ))}
          </ul>
        </div>
      </section>

      {/* What we do (services) */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy text-center">Our full-stack cashiering service</h2>
          <p className="mt-3 text-base text-slate-500 text-center max-w-2xl mx-auto">Everything a CLC-regulated conveyancing firm needs — delivered inside InTouch.</p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(lc.services || []).map((s, i) => {
              const Icon = getIcon(s.icon);
              return (
                <Link key={s.slug} to={`/legal-cashiering/${s.slug}`} data-testid={`lc-service-${s.slug}`} className="group bg-white rounded-2xl border border-navy/5 shadow-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
                  <span className="w-12 h-12 rounded-xl bg-navy text-white flex items-center justify-center mb-4 group-hover:bg-teal transition-colors"><Icon size={22} /></span>
                  <h3 className="font-display font-bold text-lg text-navy leading-snug">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.intro}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-teal group-hover:gap-2.5 transition-all">Learn more <ArrowRight size={15} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy text-center">{lc.how_it_works?.heading}</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(lc.how_it_works?.steps || []).map((st, i) => (
              <div key={i} className="relative bg-slate-50 rounded-2xl p-6 border border-navy/5">
                <span className="font-display font-extrabold text-4xl text-teal/20">0{i + 1}</span>
                <h3 className="font-display font-bold text-lg text-navy mt-2">{st.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{st.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why + InTouch */}
      <section className="py-20 md:py-24 numbers-pattern text-white relative">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl">{lc.why?.heading}</h2>
            <div className="mt-8 space-y-5">
              {(lc.why?.points || []).map((p, i) => (
                <div key={i} className="flex items-start gap-3"><CheckCircle2 size={20} className="text-teal shrink-0 mt-0.5" /><div><p className="font-display font-bold text-white">{p.title}</p><p className="text-sm text-slate-300">{p.text}</p></div></div>
              ))}
            </div>
          </div>
          <div className="bg-white/[0.06] backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h3 className="font-display font-bold text-2xl text-white">{lc.intouch?.heading}</h3>
            <p className="mt-4 text-slate-300 leading-relaxed">{lc.intouch?.text}</p>
          </div>
        </div>
      </section>

      {/* InTouch + Xero integration & partnership */}
      {lc.integration && (
        <section className="py-20 md:py-24 bg-white" data-testid="lc-integration">
          <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {lc.integration.eyebrow && <span className="text-teal font-bold text-sm tracking-widest uppercase">{lc.integration.eyebrow}</span>}
              <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy mt-3">{lc.integration.heading}</h2>
              {lc.integration.partner_badge && (
                <span className="mt-4 inline-flex items-center gap-2 bg-teal/10 text-teal font-bold text-xs uppercase tracking-wider px-3 py-1.5 rounded-full" data-testid="lc-partner-badge">
                  <CheckCircle2 size={14} /> {lc.integration.partner_badge}
                </span>
              )}
              <p className="mt-5 text-base text-slate-500 leading-relaxed">{lc.integration.intro}</p>
              <ul className="mt-6 space-y-3">
                {(lc.integration.points || []).map((p, i) => (
                  <li key={i} className="flex items-start gap-3"><CheckCircle2 size={18} className="text-teal shrink-0 mt-0.5" /> <span className="text-sm font-semibold text-navy">{p}</span></li>
                ))}
              </ul>
            </div>
            <div className="flex items-center justify-center gap-4 sm:gap-6">
              <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-lg flex items-center justify-center w-40 sm:w-52 h-32 sm:h-36">
                <img src={intouchLogo} alt="InTouch — legal case management & accounts" className="w-full h-full object-contain" loading="lazy" />
              </div>
              <span className="font-display font-extrabold text-3xl text-slate-300">+</span>
              <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-lg flex items-center justify-center w-40 sm:w-52 h-32 sm:h-36">
                <img src={xeroLogo} alt="Xero — Platinum Partner" className="w-full h-full object-contain" loading="lazy" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Lead magnet */}
      {lc.lead_magnet?.enabled && (
        <section id="lead-magnet" className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="bg-white rounded-3xl border border-navy/5 shadow-lg p-8 flex flex-col md:flex-row items-center gap-6">
              <span className="w-14 h-14 rounded-2xl bg-teal/10 text-teal flex items-center justify-center shrink-0"><Download size={26} /></span>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-display font-bold text-xl text-navy">{lc.lead_magnet.heading}</h3>
                <p className="text-sm text-slate-500 mt-1">{lc.lead_magnet.text}</p>
              </div>
              {lc.lead_magnet.file_url ? (
                <button type="button" onClick={() => setLmOpen(true)} data-testid="lc-lead-magnet-cta" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold text-sm px-6 py-3.5 rounded-full transition-all shrink-0"><Download size={16} /> Get the checklist</button>
              ) : lc.lead_magnet.guide_slug ? (
                <Link to={`/insights`} data-testid="lc-lead-magnet-cta" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold text-sm px-6 py-3.5 rounded-full transition-all shrink-0"><Download size={16} /> Get the checklist</Link>
              ) : (
                <a href="#enquire" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold text-sm px-6 py-3.5 rounded-full transition-all shrink-0">Request it <ArrowRight size={16} /></a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Success Stories strip */}
      {lc.case_studies?.filter((c) => c.published !== false && c.slug).length > 0 && (
        <section className="py-20 md:py-24 bg-slate-50" data-testid="lc-case-studies">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-teal font-bold text-sm tracking-widest uppercase">Client Success Stories</span>
              {lc.case_studies_heading && <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-navy">{lc.case_studies_heading}</h2>}
              {lc.case_studies_intro && <p className="mt-4 text-base text-slate-500 leading-relaxed">{lc.case_studies_intro}</p>}
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              {lc.case_studies.filter((c) => c.published !== false && c.slug).map((c, i) => (
                <Link key={c.slug} to={`/legal-cashiering/case-study/${c.slug}`} data-testid={`lc-case-study-card-${i}`}
                  className="group w-full max-w-sm bg-white rounded-2xl border border-navy/5 shadow-sm p-7 flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  {c.logo
                    ? <img loading="lazy" decoding="async" src={mediaUrl(c.logo)} alt={c.client} className="h-12 w-12 rounded-lg object-contain mb-5" />
                    : <div className="h-12 w-12 rounded-lg bg-teal/10 text-teal font-display font-extrabold text-xl flex items-center justify-center mb-5">{(c.client || "C").charAt(0)}</div>}
                  {c.sector && <span className="text-xs font-bold uppercase tracking-wide text-teal mb-2">{c.sector}</span>}
                  <h3 className="font-display font-bold text-lg text-navy leading-snug">{c.title || c.client}</h3>
                  {c.card_summary && <p className="mt-3 text-sm text-slate-500 leading-relaxed flex-1">{c.card_summary}</p>}
                  <span className="mt-5 inline-flex items-center gap-2 text-teal font-bold text-sm group-hover:gap-3 transition-all">Read case study <ArrowRight size={16} /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Meet the Founder (shared with homepage — edited in main admin) */}
      {founder && <FounderSection founder={founder} accent="teal" />}

      {/* FAQs */}
      {lc.faqs?.length > 0 && (
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy text-center mb-6">Frequently asked questions</h2>
            <div className="relative max-w-md mx-auto mb-10">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={faqQuery}
                onChange={(e) => setFaqQuery(e.target.value)}
                placeholder="Search the FAQs…"
                data-testid="lc-faq-search"
                className="w-full pl-11 pr-10 py-3 rounded-full border border-navy/10 bg-slate-50 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal/40 focus:border-teal transition-all"
              />
              {faqQuery && (
                <button onClick={() => setFaqQuery("")} data-testid="lc-faq-search-clear" aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy text-lg leading-none">×</button>
              )}
            </div>
            {(() => {
              const q = faqQuery.trim().toLowerCase();
              const matches = lc.faqs
                .map((f, i) => ({ ...f, _i: i }))
                .filter((f) => !q || `${f.question} ${f.answer}`.toLowerCase().includes(q));
              if (!matches.length) {
                return <p className="text-center text-slate-400" data-testid="lc-faq-empty">No FAQs match "{faqQuery}". Try a different word, or <a href="#enquire" className="text-teal font-semibold hover:underline">ask us directly</a>.</p>;
              }
              const grouped = {};
              matches.forEach((f) => {
                const cat = f.category || "General";
                (grouped[cat] = grouped[cat] || []).push(f);
              });
              const order = lc.faq_category_order?.length ? lc.faq_category_order : Object.keys(grouped);
              const cats = [...order.filter((c) => grouped[c]), ...Object.keys(grouped).filter((c) => !order.includes(c))];
              return (
                <div className="space-y-10">
                  {cats.map((cat) => (
                    <div key={cat} data-testid={`lc-faq-group-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <h3 className="font-display font-bold text-lg text-teal uppercase tracking-wide mb-4">{cat}</h3>
                      <div className="space-y-4">
                        {grouped[cat].map((f) => (
                          <details key={f._i} open={!!q} className="group bg-slate-50 rounded-xl border border-navy/5 p-5" data-testid={`lc-faq-${f._i}`}>
                            <summary className="font-display font-bold text-navy cursor-pointer flex items-center justify-between gap-3">{f.question}<span className="text-teal group-open:rotate-45 transition-transform text-xl leading-none">+</span></summary>
                            <p className="mt-3 text-sm text-slate-500 leading-relaxed">{f.answer}</p>
                            {f.link_url && (
                              f.link_url.startsWith("/") ? (
                                <Link to={f.link_url} data-testid={`lc-faq-link-${f._i}`} className="mt-4 inline-flex items-center gap-2 bg-navy hover:bg-teal text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">
                                  {f.link_label || "Learn more"} <ArrowRight size={15} />
                                </Link>
                              ) : (
                                <a href={f.link_url} target="_blank" rel="noopener noreferrer" data-testid={`lc-faq-link-${f._i}`} className="mt-4 inline-flex items-center gap-2 bg-navy hover:bg-teal text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">
                                  {f.link_label || "Learn more"} <ArrowRight size={15} />
                                </a>
                              )
                            )}
                          </details>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* The answer library — client account rules answered once, in one place,
          for CLC and SRA firms alike. */}
      <AskCassieRelated
        category="solicitors-accounts"
        topic="client account reconciliation CLC Accounts Code SRA HOFA COFA legal cashiering conveyancing"
        heading="Client account questions, answered"
        limit={5}
      />

      {/* Enquiry / CTA */}
      <section id="enquire" className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy">{lc.cta?.heading}</h2>
            <p className="mt-4 text-base text-slate-500 leading-relaxed">{lc.cta?.text}</p>
            <p className="mt-6 text-sm font-semibold text-slate-400">Part of {SITE_NAME}</p>
          </div>
          <EnquiryForm />
        </div>
      </section>

      <LeadMagnetModal lm={lc.lead_magnet} open={lmOpen} onClose={() => setLmOpen(false)} />
    </div>
  );
}
